import { Theme as AugmentedTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import React, { useEffect } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  Button,
  ContentTemplate,
  Grid,
  KeyValue,
  Link,
  Progress,
  SectionHeader,
  TitleHeader,
  Typography,
} from 'web-library';
import RouteKey from '../../routes/key';
import { useWOGAA } from '../../hooks/wogaa';

import { UpdateResitCandidateSelectedSubjects } from '../../store/resitSlice';
import { useGetCoordinatorAppealPaymentSuccessDetails } from '../../api-refactored/appeal/useGetCoordinatorAppealPaymentSuccessDetails';
import { stringToLongDateAndShortTime } from '../../utils/DateUtils';
import ReceiptDetail from '../../modules/appeal/components/@candidate/receipt/ReceiptDetail';

const { appeal, paymentLink } = RouteKey.child;
const { applicationsCoordinator, summary } = RouteKey.appeal.child;
const { appealRegistration } = RouteKey['payment-link'].child;

const useStyles = makeStyles()((theme) =>
({
  title: {
    marginBottom: theme.spacing(4),
  },
  viewSummaryText: {
    fontWeight: 800,
    paddingLeft: theme.spacing(1),
  },
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(7),
    marginRight: theme.spacing(2),
  },
  coordinatorAppealOutcomeStatement: {
    marginBottom: theme.spacing(4),
  },
}));

const coordinatorAppealReceiptStatement = (
  lastPartOfUrl: string,
  classes: Record<string,string>,
  t: TFunction<'translation'>
) => (
  <Typography className={classes.coordinatorAppealOutcomeStatement} variant="body1">
    {lastPartOfUrl === 'receipt'
      ? t('exam->>RegistrationSummary.PaymentLinkMessage')
      : t('appeal->>AppealReceipt.AppealOutcomeStatement')}
  </Typography>
);

interface CoordinatorAppealReceiptProp {
  isPaymentLink?: boolean;
}

const CoordinatorAppealReceiptAndPaymentLink = ({
  isPaymentLink,
}: CoordinatorAppealReceiptProp) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { uuid } = useParams<{ uuid: string }>();

  const { transactionEnd } = useWOGAA('myexamsseab-6474');

  /** WOGAA complete transaction for apply appeal by coordinator flow
   * */
  transactionEnd(uuid);

  const {
    data: paymentSuccessDetails,
    isLoading: isLoadingPaymentSuccessDetails,
  } = useGetCoordinatorAppealPaymentSuccessDetails({ uuid });

  useEffect(() => {
    dispatch(UpdateResitCandidateSelectedSubjects([]));
  }, [dispatch]);

  const url = window.location.href;
  const lastPartOfUrl = url.substr(url.lastIndexOf('/') + 1);

  if (isLoadingPaymentSuccessDetails) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      {!isPaymentLink && (
        <TitleHeader
          state="success"
          title={t('appeal->>AppealReceipt.SubmittedAppeal')}
          largeContentContainer
        />
      )}
      <ContentTemplate>
        {paymentSuccessDetails?.internalTransactionRef ? (
          <ReceiptDetail
            transaction={{
              transactionNumber: paymentSuccessDetails?.transactionNumber || '',
              internalTransactionRef: paymentSuccessDetails?.internalTransactionRef,
              totalFee: paymentSuccessDetails?.totalFee || '0',
              paymentMethod: paymentSuccessDetails?.paymentMethod || '',
              paymentTimeStamp: paymentSuccessDetails?.paymentTimeStamp || '',
              gstRegNo: paymentSuccessDetails?.gstRegNo,
            }}
            toSummary={() => {
              history.push(`/${paymentLink}/${appealRegistration}/${uuid}/${summary.route}`);
            }}
          />
        ) : (
          <>
            <Grid container>
              <Grid xs={12}>
                <SectionHeader
                  title={t('appeal->>AppealReceipt.PaymentSummary')}
                  className={classes.title}
                >
                  <Link
                      id="link-viewFullSummary"
                      className={classes.viewSummaryText}
                      onClick={() =>
                          history.push(
                              `/${appeal}/${applicationsCoordinator.route}/${uuid}/${summary.route}`
                          )
                      }
                  >
                    {t('appeal->>AppealReceipt.ViewFullSummary')}
                  </Link>
                </SectionHeader>
              </Grid>
            </Grid>
            {coordinatorAppealReceiptStatement(lastPartOfUrl, classes, t)}
            <Typography className={classes.title} variant="h3">
              {t('appeal->>AppealCoordinatorReceipt.TransactionDetails')}
            </Typography>
            <Grid container>
              <Grid xs={12} sm={6}>
                <KeyValue
                  keyText={t('appeal->>AppealCoordinatorReceipt.ApplicationId')}
                  valueText={paymentSuccessDetails?.applicationId}
                />
              </Grid>
            </Grid>
            <Grid container>
              <Grid xs={12} sm={6}>
                <KeyValue
                  keyText={t('appeal->>AppealCoordinatorReceipt.ApplicationDateTime')}
                  valueText={stringToLongDateAndShortTime(
                    paymentSuccessDetails?.applicationDateTime || ''
                  )}
                />
              </Grid>
            </Grid>
          </>
        )}

        {!isPaymentLink && (
          <Button
            id="button-appealReceipt-goToRegisteredExams"
            className={classes.button}
            onClick={() => history.push(`/${appeal}/${applicationsCoordinator.route}`)}
          >
            {t('appeal->>AppealReceipt.BackToAppealApplications')}
          </Button>
        )}
      </ContentTemplate>
    </>
  );
};

export default CoordinatorAppealReceiptAndPaymentLink;
